import { useEffect, useState } from "react";

import {
  isOptionActive,
  useLocalVideo,
  useMeetingManager,
  useMeetingStatus,
  useVideoInputs,
} from "amazon-chime-sdk-component-library-react";

import { CustomPopoverItemProps, CustomPopover } from "../../bars/web-control-bar/components/custom-popover";
import { DeviceType } from "amazon-chime-sdk-component-library-react/lib/types";

interface CameraProps {
  cameraType?: string;
  subCameraType?: string;
  onCameraFound?: (found: string | undefined) => void;
  triggerVariant?: "icon" | "button";
  triggerLabel?: string;
  fullWidth?: boolean;
}

export const findDeviceFromList = (
  devices: DeviceType[],
  deviceTypeList: string[],
  subDeviceType?: string | undefined
) => {
  deviceTypeList = deviceTypeList.map(item => item.toLowerCase())

  if (subDeviceType) {
    subDeviceType = subDeviceType.toLowerCase();
  }

  let foundDevice: string | undefined = undefined;
  let foundDeviceName: string | undefined = undefined;

  for (let i = 0; i < devices.length; i++) {
    const lowerCaseName = devices[i].label.toLowerCase();

    for (let deviceType of deviceTypeList) {
      if (!lowerCaseName.includes(deviceType)) {
        continue;
      }

      foundDevice = devices[i].deviceId;
      foundDeviceName = devices[i].label;

      if (!subDeviceType) {
        break;
      }

      if (lowerCaseName.includes(subDeviceType)) {
        break;
      }
    }
  }

  return {
    foundDevice,
    foundDeviceName,
  };
};

const SelectCameraPopover: React.FC<CameraProps> = ({
  cameraType,
  subCameraType,
  onCameraFound,
  triggerVariant,
  triggerLabel,
  fullWidth,
}) => {
  const meetingManager = useMeetingManager();
  const { devices, selectedDevice } = useVideoInputs();
  const { isVideoEnabled } = useLocalVideo();

  const status = useMeetingStatus();

  const [videoOptions, setVideoOptions] = useState<CustomPopoverItemProps[]>(
    []
  );

  const [enabledBefore, setEnabledBefore] = useState<boolean>(false);

  // handles starting with video
  useEffect(() => {
    if (status != 1) {
      return;
    }

    if (!isVideoEnabled) {
      return;
    }

    if (enabledBefore || !cameraType) {
      return;
    }

    const result = findDeviceFromList(devices, [cameraType], subCameraType);

    if (result.foundDevice) {
      changeSelectedOption(result.foundDevice);
    }

    if (onCameraFound) {
      onCameraFound(result.foundDeviceName);
    }

    setEnabledBefore(true);
  }, [isVideoEnabled]);

  const onSelectChanged = (selectedOption: CustomPopoverItemProps) => {
    changeSelectedOption(selectedOption.id);
  };

  const changeSelectedOption = async (deviceId: string) => {
    try {
      if (isVideoEnabled) {
        await meetingManager.startVideoInputDevice(deviceId);
      } else {
        meetingManager.selectVideoInputDevice(deviceId);
      }
    } catch (error) {
      console.log("VideoInputControl failed to select video input device");
    }
  };

  // Handles drop down menu options
  useEffect(() => {
    const alternateGetDropDown = async () => {
      const deviceList: CustomPopoverItemProps[] = [];

      devices.map(async (device) => {
        const popoverItem: CustomPopoverItemProps = {
          label: device.label,
          id: device.deviceId,
          active: await isOptionActive(device, device.deviceId),
        };

        deviceList.push(popoverItem);
      });

      setVideoOptions(deviceList);
    };

    alternateGetDropDown();
  }, [
    devices,
    selectedDevice,
    isVideoEnabled,
    meetingManager,
    meetingManager.startVideoInputDevice,
  ]);

  return (
    <CustomPopover
      options={videoOptions}
      selectedOption={selectedDevice?.toString() || ""}
      setSelectedOption={onSelectChanged}
      openPopOverLabel="Select Camera"
      triggerVariant={triggerVariant}
      triggerLabel={triggerLabel}
      fullWidth={fullWidth}
    />
  );
};

export default SelectCameraPopover;
